'use client';

import { motion } from "framer-motion";
import Image from "next/image";
import { cn, containerClasses } from "@/lib/utils";
import { DebugEditButton } from '@/components/mcp/debug/DebugEditButton';

interface Step {
  title: string;
  description: string;
  image: string;
}

const StepCard = ({ 
  step, 
  index, 
  total 
}: { 
  step: Step; 
  index: number;
  total: number;
}) => {
  const isEven = index % 2 === 0;
  const isLast = index === total - 1;
  const isLater = index >= 3;

  return (
    <motion.div 
      variants={{
        hidden: { opacity: 0, y: 20 },
        show: { opacity: 1, y: 0 }
      }}
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.3 }}
      className={cn(
        "grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-20",
        !isLast && "mb-16 md:mb-24",
        isLater && "mt-16 md:mt-24"
      )}
    >
      <motion.div 
        className={cn(
          "flex flex-col justify-center",
          !isEven && "md:order-2",
          !isEven ? "px-2" : "px-6"
        )}
        whileHover={{ y: -5 }}
        transition={{ duration: 0.3 }}
      >
        <h3 className={cn(
          "text-h3 mb-4 md:mb-6",
          "whitespace-pre-line"
        )}>
          {step.title}
        </h3>
        <p className={cn(
          "text-body",
          "max-w-[580px]"
        )}>
          {step.description}
        </p>
      </motion.div>

      <motion.div 
        className={cn(
          !isEven && "md:order-1",
          "bg-floral-white rounded-xl relative overflow-hidden",
          "hover:shadow-neumorphic h-[300px] w-full",
          "transition-all duration-300"
        )}
        whileHover={{ scale: 1.02 }}
        transition={{ duration: 0.3 }}
      >
        <div className="relative h-full w-full group">
          <Image 
            src={step.image}
            alt={`Step ${index + 1}`}
            width={1476}
            height={695}
            className="relative z-10 w-full h-full object-cover object-center"
            priority={index < 2}
          />
          
          {/* Debug edit button - only shown in debug mode and on hover */}
          <DebugEditButton 
            imageName={step.image.split('/').pop()?.split('.')[0] || ''}
            imageUrl={step.image}
          />
        </div>
      </motion.div>
    </motion.div>
  );
};

export function BenefitsAndStepsPage() {
  const content = {
    title: "Wie umynd dich unterstützt:",
    steps: [
        { title: "Anforderungsanalyse", description: "Wir bringen unsere Expertise über den Bewerbermarkt in jede Besetzung mit ein:\n\n\n\n\nWir setzen uns vor dem Beginn der Suche ausführlich mit dem Unternehmen und seiner spezifischen Situation auseinander.\n\n\n\n\nEntsprechend lernen wir jeden Kandidaten ausführlich kennen und ergründen seine Fähigkeiten und Präferenzen, denn wir wollen, dass Ihr zueinander passt!", image: "/section-requirements.png" },
        { title: "Aktive Suche für Unternehmen und Kandidaten", description: "Auf Basis unserer Kenntnis von Unternehmen und Kandidaten nutzen wir unsere proprietäre KI-Technologie für die Suche und Erstansprache von Kandidaten. Umgekehrt unterstützen wir Kandidaten bei der Suche nach passenden Jobs und Unternehmen.", image: "/section-active-search.png" },
        { title: "KI-gestütztes Matching", description: "Haben wir eine grundsätzliche Passung zwischen Unternehmen und Kandidat gefunden, gehen wir weiter und ergründen in vertieften Interviews detailliert die fachliche Eignung und den wechselseitigen kulturellen \"Fit\". Wir nutzen KI, wo immer möglich und akzeptiert - aber wir begleiten und evaluieren alle Schritte mit menschlicher Expertise (\"Human-in-the-Loop\")", image: "/section-matching.png" },
        { title: "Kontinuierliche Begleitung", description: "Im gesamten Prozess kommunizieren wir engmaschig mit unseren Kunden und Kandidaten. Beide sollen jederzeit über den Stand des Prozesses auf dem Laufenden sein. Ebenso erhält jeder Kandidat ein inhaltliches Feedback zur Entscheidung.", image: "/section-coaching.png" },
        { title: "Langfristige Partnerschaft", description: "Unsere Systeme sind so aufgesetzt, dass sie permanent dazulernen - über unsere Kunden und deren Kandidaten. Unsere Zielsetzung sind langfristige Partnerschaften mit beiden Anspruchsgruppen. Wir geben unseren Kunden die Garantie, dass wir einmal platzierte Kandidaten nicht aktiv auf weitere Vakanzen ansprechen. Kandidaten, die zunächst nicht platziert werden konnten, begleiten wir bei der weiteren Suche.", image: "/section-cooperation.png" }
    ]
  };

  const {
    title,
    steps
  } = content;

  return (
    <section className={cn(
      "py-24 bg-black-50 font-gilroy"
    )}>
      <div className={containerClasses}>
        <motion.div
          variants={{
            hidden: { opacity: 0 },
            show: {
              opacity: 1,
              transition: {
                staggerChildren: 0.2
              }
            }
          }}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="mx-auto"
        >
          <motion.h2 
            variants={{
              hidden: { opacity: 0, y: 20 },
              show: { opacity: 1, y: 0 }
            }}
            className={cn(
              "text-h1 mb-20",
              "text-center sm:text-left whitespace-pre-line"
            )}
          >
            {title}
          </motion.h2>

          {steps.map((step, index) => (
            <StepCard
              key={index}
              step={step}
              index={index}
              total={steps.length}
            />
          ))}
        </motion.div>
      </div>
    </section>
  );
}